<script setup>
import { computed, getCurrentInstance, ref } from "vue";
import { useI18n } from "vue-i18n";
import { CheckIcon } from "@heroicons/vue/20/solid";
import { RadioGroup, RadioGroupOption } from "@headlessui/vue";
import { useStore } from "@Stores/pricingPlans";
import { useStore as useDomainStore } from "@Stores/domain";
import LoadingAnimation from "@Components/Animation/Loading";
import Modal from "./Modal.vue";
import { PrimaryButton } from "@Components/Button";

const store = useStore();
const domainStore = useDomainStore();
const { t: __ } = useI18n();

const isLoaded = computed(() => store.isLoaded);
const products = computed(() => store.products);

const frequency = ref("monthly");

const gridClasses = (length, index) => {
    switch (length) {
        case 1:
            return "md:col-start-3 md:col-span-2";
        case 2:
            if (index === 0) {
                return "md:col-start-2 md:col-span-2";
            }
            return "md:col-span-2";
        default:
            return "md:col-span-2";
    }
};

const frequencies = computed(() => {
    let f = {};
    products.value.forEach((product) => {
        product.prices.forEach((price) => {
            if (price.billing_cycle === "monthly") {
                f["monthly"] = { label: "Monthly", priceSuffix: "ht" };
            }
            if (price.billing_cycle === "yearly") {
                f["yearly"] = { label: "Yearly", priceSuffix: "ht" };
            }
            if (price.billing_cycle === "lifetime") {
                f["lifetime"] = { label: "Lifetime", priceSuffix: "ht" };
            }
        });
    });

    if (f.lifetime == undefined) {
        f.lifetime = { label: "Lifetime", priceSuffix: "ht" };
    }

    return f;
});

const domain = computed(() => domainStore.domain);

const isSubscribed = computed(() => {
    return domain.value?.user?.subscription?.status === "active";
});

const subscriptionProductId = computed(() => {
    if (isSubscribed.value) {
        return domain.value?.user?.subscription?.product?.id;
    }
});

const tiers = computed(() => {
    let t = [];
    products.value.forEach((product) => {
        product.prices
            .filter((price) => {
                return price.billing_cycle === frequency.value;
            })
            .forEach((price) => {
                t.push({
                    name: product.name,
                    id: product.id,
                    price_amount: price.amount,
                    price: "$" + price.amount,
                    crossed_out_price: price.crossed_out_amount ? "$" + price.crossed_out_amount : undefined,
                    description: product.description,
                    is_featured: price.is_featured,
                    frequency: price.billing_cycle,
                    features: product.features,
                    link: price.link,
                });
            });
    });

    t.sort((a, b) => {
        return a.price_amount - b.price_amount;
    });

    return t;
});

const modal = ref();
const show = () => {
    modal.value?.show();
};

const hide = () => {
    modal.value?.hide();
};

defineExpose({ show, hide });
</script>

<template>
    <Modal ref="modal" class="sm:max-w-4xl">
        <div class="mx-auto px-6 py-10 lg:px-8">
            <div class="mx-auto max-w-4xl text-center">
                <div class="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">Pricing plans</div>
            </div>
            <div v-if="!isLoaded" class="flex justify-center items-center mt-10">
                <LoadingAnimation class="w-20 h-20" />
            </div>
            <div v-else class="mt-8">
                <div class="flex justify-center">
                    <RadioGroup
                        v-model="frequency"
                        :class="[
                            'grid p-1 text-xs font-semibold leading-5 text-center rounded-full gap-x-1 ring-1 ring-inset ring-gray-300 dark:ring-gray-700',
                            Object.keys(frequencies).length === 2 ? 'grid-cols-2' : Object.keys(frequencies).length === 3 ? 'grid-cols-3' : 'grid-cols-1',
                        ]">
                        <RadioGroupOption as="template" v-for="(option, key) in frequencies" :key="key" :value="key" v-slot="{ checked }">
                            <div
                                :class="[
                                    checked ? 'bg-gray-900 text-white dark:bg-gray-700' : 'text-gray-600 dark:text-gray-400',
                                    'cursor-pointer rounded-full px-2.5 py-1',
                                ]">
                                <span>{{ option.label }}</span>
                            </div>
                        </RadioGroupOption>
                    </RadioGroup>
                </div>
                <div class="mx-auto max-w-md isolate grid grid-cols-1 gap-8 mt-10 md:max-w-4xl md:grid-cols-6">
                    <template v-for="(tier, index) in tiers" :key="index">
                        <div
                            :class="[
                                tier.is_featured ? 'ring-2 ring-gray-900 dark:ring-gray-700' : 'ring-1 ring-gray-300 dark:ring-gray-700',
                                'rounded-3xl p-8',
                                gridClasses(frequency === 'lifetime' ? tiers.length + 1 : tiers.length, index),
                            ]">
                            <h2 :id="tier.id" :class="['text-gray-900 dark:text-white text-lg font-semibold leading-8']">
                                {{ tier.name }}
                            </h2>
                            <p class="mt-4 text-sm leading-6 text-gray-600 dark:text-gray-400">
                                {{ tier.description }}
                            </p>
                            <p class="flex flex-col justify-center items-center gap-y-1 mt-6">
                                <span v-if="tier.crossed_out_price" class="text-2xl font-bold tracking-tight text-gray-600 line-through dark:text-gray-400">
                                    {{ tier.crossed_out_price }}
                                </span>
                                <span class="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">{{ tier.price }}</span>
                            </p>
                            <div class="mt-6">
                                <div
                                    v-if="isSubscribed && subscriptionProductId === tier.id"
                                    :class="[
                                        ' shadow-sm text-gray-900 dark:text-white ring-2 ring-gray-900 dark:ring-gray-700',
                                        'mt-6 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6',
                                    ]">
                                    {{ __("Votre abonnement") }}
                                </div>
                                <PrimaryButton
                                    :z="1"
                                    v-else-if="isSubscribed"
                                    as="a"
                                    href="https://studio.otomatic.ai/user/billing/manage"
                                    target="_blank"
                                    class="block text-center">
                                    {{ __("Mettre à niveau") }}
                                </PrimaryButton>

                                <PrimaryButton :z="1" v-else as="a" :href="tier.link" target="_blank" class="block text-center">
                                    {{ __("Souscrire") }}
                                </PrimaryButton>
                            </div>
                            <ul role="list" class="mt-8 space-y-3 text-sm leading-6 text-gray-600 dark:text-gray-400">
                                <li v-for="feature in tier.features" :key="feature" class="flex gap-x-3 text-gray-600 dark:text-gray-400">
                                    <CheckIcon class="w-5 h-6 flex-none text-gray-900 dark:text-white" aria-hidden="true" />
                                    {{ feature }}
                                </li>
                            </ul>
                        </div>
                    </template>
                    <div
                        v-if="frequency === 'lifetime'"
                        :class="['ring-2 ring-gray-900 dark:ring-gray-700', 'rounded-3xl p-8', gridClasses(tiers.length + 1, index)]">
                        <h2 :class="['text-gray-900 dark:text-white text-lg font-semibold leading-8']">Lifetime Bundle Pack</h2>
                        <p class="mt-4 text-sm leading-6 text-gray-600 dark:text-gray-400"></p>
                        <p class="flex flex-col justify-center items-center gap-y-1 mt-6">
                            <span class="text-4xl font-bold tracking-tight text-gray-900 dark:text-white"> $1990 </span>
                        </p>
                        <div class="mt-6">
                            <PrimaryButton :z="1" as="a" href="https://market.freudix.studio/register" target="_blank" class="block text-center">
                                {{ __("Acheter") }}
                            </PrimaryButton>
                        </div>
                        <div class="mt-8">
                            <div class="text-sm font-semibold leading-6 text-gray-900 dark:text-white">Inclus les lifetimes de :</div>
                            <ul role="list" class="mt-2 space-y-3 text-sm leading-6 text-gray-600 dark:text-gray-400">
                                <li class="flex items-center gap-x-2">
                                    <img
                                        class="w-auto h-6 rounded-sm"
                                        :src="getCurrentInstance().appContext.config.globalProperties.$asset('img/bundled-products/otomatic-ai.png')"
                                        alt="otomatic.ai" />
                                    <a href="https://otomatic.ai" target="_blank" class="text-gray-600 dark:text-gray-400 hover:underline"
                                        >otomatic.ai Agence</a
                                    >
                                </li>
                                <li class="flex items-center gap-x-2">
                                    <img
                                        class="w-6 h-auto rounded-sm"
                                        :src="getCurrentInstance().appContext.config.globalProperties.$asset('img/bundled-products/wptranslation.png')"
                                        alt="WPTranslation" />
                                    <a href="https://wptranslation.net/" target="_blank" class="text-gray-600 dark:text-gray-400 hover:underline"
                                        >WPTranslation</a
                                    >
                                </li>
                                <li class="flex items-center gap-x-2">
                                    <img
                                        class="w-6 h-auto rounded-sm"
                                        :src="getCurrentInstance().appContext.config.globalProperties.$asset('img/bundled-products/wptemplate.png')"
                                        alt="WPTemplate" />
                                    <a href="https://wptemplate.studio/" target="_blank" class="text-gray-600 dark:text-gray-400 hover:underline">WPTemplate</a>
                                </li>
                                <li class="flex items-center gap-x-2">
                                    <img
                                        class="w-6 h-auto rounded-sm"
                                        :src="getCurrentInstance().appContext.config.globalProperties.$asset('img/bundled-products/domain-finder.png')"
                                        alt="Domain finder" />
                                    <a href="https://domain-finder.net/" target="_blank" class="text-gray-600 dark:text-gray-400 hover:underline"
                                        >Domain finder</a
                                    >
                                </li>
                                <li class="flex items-center gap-x-2">
                                    <img
                                        class="w-6 h-auto rounded-sm"
                                        :src="getCurrentInstance().appContext.config.globalProperties.$asset('img/bundled-products/freudix-academy.png')"
                                        alt="freudix.academy" />
                                    <a href="https://freudix.academy/" target="_blank" class="text-gray-600 dark:text-gray-400 hover:underline"
                                        >freudix.academy</a
                                    >
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
</template>
